import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as mealServicesApi from './meal-services.api';

import type {
  CreateMealServiceRequest,
  MealServiceApprovalRequest,
  MealServiceListQuery,
  UpdateMealServiceRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: MealServiceListQuery) => ['meal-services', 'list', query] as const,
  facets: ['meal-services', 'facets'] as const,
  detail: (id: string) => ['meal-services', 'detail', id] as const,
};

/**
 * Facets (in-use countries + cities) for the Country / City list filters.
 * Keyed under ['meal-services'] so create/update/delete invalidations refresh
 * it - a meal service added in a new city shows up in the filter automatically.
 */
export const useMealServiceFacets = () =>
  useQuery({
    queryKey: QK.facets,
    queryFn: mealServicesApi.getMealServiceFacets,
    staleTime: 60_000,
  });

export const useMealServicesList = (query: MealServiceListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => mealServicesApi.listMealServices(query),
    staleTime: 30_000,
  });

export const useMealServiceDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => mealServicesApi.getMealService(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateMealService = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMealServiceRequest) => mealServicesApi.createMealService(payload),
    onSuccess: (svc) => {
      qc.invalidateQueries({ queryKey: ['meal-services', 'list'] });
      toast.success(`Added ${svc.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add meal service'),
  });
};

export const useUpdateMealService = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMealServiceRequest }) =>
      mealServicesApi.updateMealService(id, payload),
    onSuccess: (svc) => {
      qc.invalidateQueries({ queryKey: ['meal-services', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(svc.id) });
      toast.success('Meal service updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update meal service'),
  });
};

export const useApproveMealService = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: MealServiceApprovalRequest }) =>
      mealServicesApi.approveMealService(id, payload),
    onSuccess: (svc, vars) => {
      qc.invalidateQueries({ queryKey: ['meal-services', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(svc.id) });
      toast.success(
        `${vars.payload.tier === 'admin' ? 'Admin' : 'Moderator'} ${vars.payload.approve ? 'approved' : 'approval revoked'}`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteMealService = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mealServicesApi.deleteMealService(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-services', 'list'] });
      toast.success('Meal service deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete meal service'),
  });
};

export const useBulkDeleteMealServices = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => mealServicesApi.bulkDeleteMealServices(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meal-services'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete meal services'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
